import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useFileUpload } from "@/hooks/notifications/useFileUpload";

export const useMessageHandlers = (teamId: string | undefined, setIsLoading: (loading: boolean) => void) => {
  const [newMessage, setNewMessage] = useState("");
  const { toast } = useToast();
  const { isAdmin } = useUserRoles();
  const { uploadFile } = useFileUpload();

  // Function to ensure user profile exists with proper data
  const ensureUserProfile = async (userId: string, email?: string | null) => {
    try {
      // Check if profile exists
      const { data: existingProfile, error: profileError } = await supabase
        .from("profiles")
        .select("id, full_name, first_name, last_name, email")
        .eq("id", userId)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error("Error checking profile:", profileError);
        throw profileError;
      }

      if (!existingProfile) {
        // If profile doesn't exist, create it with available information
        const { data: userData, error: userError } = await supabase.auth.getUser();
        const user = userData?.user;
        
        if (userError) {
          console.error("Error getting user data:", userError);
          throw userError;
        }
        
        const { error } = await supabase
          .from("profiles")
          .insert({
            id: userId,
            email: email || user?.email,
            full_name: user?.user_metadata?.full_name,
            first_name: user?.user_metadata?.first_name,
            last_name: user?.user_metadata?.last_name,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) {
          console.error("Error creating profile:", error);
          throw error;
        }
      } else if (!existingProfile.full_name) {
        // If profile exists but is missing full name, try to update it
        const { data: userData, error: userError } = await supabase.auth.getUser();
        const user = userData?.user;
        
        if (userError) {
          console.error("Error getting user data for full name:", userError);
        }
        
        // Generate full name from parts if available
        let fullName = user?.user_metadata?.full_name;
        if (!fullName && (existingProfile.first_name || existingProfile.last_name)) {
          fullName = `${existingProfile.first_name || ''} ${existingProfile.last_name || ''}`.trim();
        }
        
        if (fullName) {
          const { error } = await supabase
            .from("profiles")
            .update({ full_name: fullName, updated_at: new Date().toISOString() })
            .eq("id", userId);
            
          if (error) {
            console.error("Error updating profile full name:", error);
          }
        }
      }
      
      return true;
    } catch (error) {
      console.error("Error in ensureUserProfile:", error);
      return false;
    }
  };

  const handleImageUpload = async (file: File) => {
    if (!teamId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Team ID is missing",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        console.error("Auth error:", userError);
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: "You must be logged in to send messages",
        });
        setIsLoading(false);
        return;
      }

      // Ensure user profile exists before continuing
      const profileCreated = await ensureUserProfile(user.id, user.email);
      if (!profileCreated) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to validate user profile",
        });
        setIsLoading(false);
        return;
      }

      // Use the useFileUpload hook to upload the file to the team-chat-images bucket
      const publicUrl = await uploadFile(file, 'team-chat-images');
      
      if (!publicUrl) {
        console.error("Failed to upload image");
        setIsLoading(false);
        return;
      }

      console.log("Inserting message with image URL:", publicUrl);
      
      const { data, error: messageError } = await supabase
        .from("team_messages")
        .insert({
          team_id: teamId,
          content: "",
          author_id: user.id,
          image_url: publicUrl
        })
        .select();

      if (messageError) {
        console.error("Message insert error:", messageError);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to send message with image",
        });
      } else if (data && data[0]) {
        // Message was sent successfully, send a notification
        try {
          await supabase.functions.invoke('send-notification', {
            body: {
              teamId,
              messageId: data[0].id,
              title: 'New image message',
              body: 'A team member shared an image'
            }
          });
        } catch (error) {
          console.error('Error sending notification:', error);
        }
      }
    } catch (error) {
      console.error("Error in handleImageUpload:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred",
      });
    }

    setIsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMessage.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Message cannot be empty",
      });
      return;
    }
    
    if (!teamId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Team ID is missing",
      });
      return;
    }

    setIsLoading(true);
    console.log("Submitting message:", newMessage.trim());

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        console.error("Auth error:", userError);
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: "You must be logged in to send messages",
        });
        setIsLoading(false);
        return;
      }

      // Ensure user profile exists before sending message
      const profileCreated = await ensureUserProfile(user.id, user.email);
      if (!profileCreated) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to validate user profile",
        });
        setIsLoading(false);
        return;
      }

      console.log("Inserting message into database:", {
        team_id: teamId,
        content: newMessage.trim(),
        author_id: user.id
      });
      
      const { data, error: messageError } = await supabase
        .from("team_messages")
        .insert({
          team_id: teamId,
          content: newMessage.trim(),
          author_id: user.id
        })
        .select();

      if (messageError) {
        console.error("Message insert error:", messageError);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to send message: " + messageError.message,
        });
      } else {
        console.log("Message sent successfully:", data);
        setNewMessage("");
        
        // Force an immediate state update for better UX
        if (data && data[0]) {
          // Add the message optimistically to the local state
          const optimisticMessage = {
            id: data[0].id,
            content: newMessage.trim(),
            created_at: data[0].created_at || new Date().toISOString(),
            image_url: null,
            author: {
              id: user.id,
              full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'You'
            }
          };
          
          // Trigger a manual update via a custom event
          window.dispatchEvent(new CustomEvent('newTeamMessage', { 
            detail: { message: optimisticMessage, teamId } 
          }));
        }
        
        // If message was inserted successfully, send a notification
        if (data && data[0]) {
          try {
            await supabase.functions.invoke('send-notification', {
              body: {
                teamId,
                messageId: data[0].id,
                title: 'New team message',
                body: newMessage.trim().substring(0, 100) + (newMessage.length > 100 ? '...' : '')
              }
            });
          } catch (error) {
            console.error('Error sending notification:', error);
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred",
      });
    }

    setIsLoading(false);
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      // Check if user is admin before attempting to delete
      if (!isAdmin) {
        toast({
          variant: "destructive",
          title: "Permission Denied",
          description: "Only administrators can delete messages.",
        });
        return;
      }

      const { error } = await supabase
        .from("team_messages")
        .delete()
        .eq("id", messageId);

      if (error) {
        console.error("Error deleting message:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to delete message.",
        });
      } else {
        toast({
          title: "Success",
          description: "Message deleted successfully.",
        });
        
        // Return true to indicate successful deletion for UI update
        return true;
      }
    } catch (error) {
      console.error("Error in handleDeleteMessage:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An error occurred while deleting the message",
      });
    }
    
    // Return false if deletion failed
    return false;
  };

  return {
    newMessage,
    setNewMessage,
    handleSubmit,
    handleDeleteMessage,
    handleImageUpload,
    handleEditMessage: async (messageId: string, newContent: string) => {
      if (!newContent.trim()) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Message cannot be empty",
        });
        return;
      }

      try {
        const { error } = await supabase
          .from("team_messages")
          .update({
            content: newContent.trim(),
            updated_at: new Date().toISOString(),
          })
          .eq("id", messageId);

        if (error) {
          console.error("Error updating message:", error);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to update message",
          });
        } else {
          toast({
            title: "Success",
            description: "Message updated successfully",
          });
        }
      } catch (error) {
        console.error("Error in handleEditMessage:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "An unexpected error occurred",
        });
      }
    },
  };
};
